import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import { useTranslation } from 'next-i18next/pages';
import React from 'react';
import 'swiper/css';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import useSwiperNavigation from '@/hooks/shared/useSwiperNavigation';

import Area from '@/components/apps/layouts/shared/Area';
import {
  IconArrowLeftRound,
  IconArrowRightRound,
} from '@/components/apps/ui/icon';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import ButtonIcon from '@/components/shared/ui/buttons/ButtonIcon';
import { trackIndustrialCaseSelection } from '@/components/apps/pages/solution/industrial/industrialAnalytics';

import theme from '@/styles/theme';

import { HomeIndustrialCaseCard } from './HomeIndustrialCaseCard';

const CONTAINER_CLASS =
  'client-use-cases-bg overflow-hidden bg-apps-coreGrayBlack bg-top bg-cover bg-no-repeat py-[100px] lg:py-[clamp(100px,16.667vw,200px)]';
const NAVIGATION_CLASS =
  'client-use-cases-nav hidden flex-none grid-cols-[1fr_1fr] gap-[8px] lg:grid';
const SWIPER_CONTAINER_CLASS =
  'client-use-cases-swiper mt-[40px] lg:mt-[clamp(40px,6.667vw,80px)]';

type Props = {
  cases: ReadonlyArray<IndustrialCaseSummary>;
};

/**
 * ### ClientUseCases
 *
 * Renders the customer-case section from published board data supplied by the
 * home route. An empty response remains an explicit empty state so the page
 * never presents translated placeholders as if they were customer evidence.
 *
 * @param props Published cases for the active locale
 * @returns The customer-case section
 */
export default function ClientUseCases({ cases }: Props) {
  const { t } = useTranslation('common');
  const { navigation, nextElRef, onBeforeInit, prevElRef } =
    useSwiperNavigation();

  return (
    <div className={CONTAINER_CLASS}>
      <Area>
        <div className="lg:flex lg:items-end lg:justify-between">
          <SectionTitle
            color="lighter"
            title={t('Listen AI를 활용한 다양한 고객 사례')}
            description={t(
              '고객사의 환경과 특수성을 고려한 사운드 인공지능 솔루션을 개발하고 실상황에 적용하고 있어요.',
            )}
          />
          {cases.length > 0 && (
            <div className={NAVIGATION_CLASS}>
              <div ref={prevElRef}>
                <ButtonIcon aria-label={t('이전 고객 사례')}>
                  <IconArrowLeftRound
                    size={32}
                    color={theme`colors.apps.coreGrayWhite`}
                    aria-hidden="true"
                  />
                </ButtonIcon>
              </div>
              <div ref={nextElRef}>
                <ButtonIcon aria-label={t('다음 고객 사례')}>
                  <IconArrowRightRound
                    size={32}
                    color={theme`colors.apps.coreGrayWhite`}
                    aria-hidden="true"
                  />
                </ButtonIcon>
              </div>
            </div>
          )}
        </div>

        {cases.length === 0 ? (
          <div className="mt-[40px] rounded-[20px] border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.04)] px-6 py-16 text-center lg:mt-[clamp(40px,6.667vw,80px)]">
            <p className="text-lg font-bold leading-[140%] text-apps-coreGrayWhite">
              {t('아직 공개된 고객 사례가 없어요')}
            </p>
            <p className="mt-3 text-[15px] font-medium leading-[160%] text-apps-brandGray700">
              {t('준비되는 대로 이곳에 올려둘게요')}
            </p>
          </div>
        ) : (
          <div className={SWIPER_CONTAINER_CLASS}>
            <Swiper
              grabCursor
              modules={[Navigation]}
              navigation={navigation}
              onBeforeInit={onBeforeInit}
              cssMode
              breakpoints={{
                768: {
                  slidesPerView: 2,
                  spaceBetween: 12,
                },
                992: {
                  slidesPerView: 3,
                  spaceBetween: 24,
                },
              }}
              className="overflow-x-auto"
            >
              {cases.map((caseSummary) => (
                <SwiperSlide key={caseSummary.slug} className="flex h-auto">
                  <HomeIndustrialCaseCard
                    caseSummary={caseSummary}
                    metricLabel={t('핵심 지표')}
                    readLabel={t('사례 읽기')}
                    onSelect={() =>
                      trackIndustrialCaseSelection({
                        contentType: 'case_card',
                        itemId: caseSummary.slug,
                        caseIdentity: {
                          slug: caseSummary.slug,
                          solution: caseSummary.solution,
                          industry: caseSummary.industry,
                        },
                      })
                    }
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </Area>
    </div>
  );
}
